import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/auth";
import Layout from "./components/layout/Layout";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import { lazy, Suspense } from "react";
import { UnifiedLoadingSpinner } from "./components/loading/UnifiedLoadingSpinner";
import { PerformanceMonitor } from "@/components/admin/monitoring/PerformanceMonitor";
import { NetworkOptimizer } from "@/components/performance/NetworkOptimizer";
import Auth from "./pages/auth/Auth";

// Import critical pages directly to avoid network issues
import { General } from "./pages/general";

import {
  ActionIcon,
  createTheme,
  DirectionProvider,
  Loader,
  MantineProvider,
} from "@mantine/core";
import SchoolLayout from "./components/layout/SchoolLayout";
import SimpleAttendanceFixed from "./pages/attendance/SimpleAttendanceFixed";
import LateAttendance from "./pages/attendance/LateAttendance";
import ReportsBeta from "./pages/reports/ReportsBeta";
import SchoolSettings from "./pages/settings/SchoolSettings";
import Students from "./pages/students/Students";
import Teachers from "./pages/teachers/Teachers";
import StudentLateAttendance from "./pages/attendance/StudentLateAttendance";
import StudentAttendance from "./pages/attendance/StudentAttendance";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ContentManagement from "./pages/general/ContentManagement";
import AdminLayout from "./components/layout/AdminLayout";

// Lazy load less critical pages for better performance
// const Students = lazy(() => import("./pages/students/Students"));
// const Teachers = lazy(() => import("./pages/teachers/Teachers"));
// const LateAttendance = lazy(() => import("./pages/attendance/LateAttendance"));
// const SimpleAttendanceFixed = lazy(
//   () => import("./pages/attendance/SimpleAttendanceFixed")
// );
// const StudentLateAttendance = lazy(
//   () => import("./pages/attendance/StudentLateAttendance")
// );
// const StudentAttendance = lazy(
//   () => import("./pages/attendance/StudentAttendance")
// );
// const Reports = lazy(() => import("./pages/reports/Reports"));
// const ReportsBeta = lazy(() => import("./pages/reports/ReportsBeta"));

// const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
// const ContentManagement = lazy(
//   () => import("./pages/general/ContentManagement")
// );
// const SchoolSettings = lazy(() => import("./pages/settings/SchoolSettings"));
const Profile = lazy(() => import("./pages/settings/Profile"));
const Contact = lazy(() => import("./pages/general/Contact"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const SchoolHelp = lazy(() => import("./pages/general/SchoolHelp"));
const Help = lazy(() => import("./pages/general/Help"));
const NotFound = lazy(() => import("./pages/general/NotFound"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgetPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 20 دقيقة - أطول
      gcTime: 10 * 60 * 1000, // 45 دقيقة - أطول
      // refetchOnWindowFocus: false,
      // refetchOnMount: false,
      // refetchOnReconnect: false,
      networkMode: "online", // فقط عند الاتصال
      retry: (failureCount, error: any) => {
        if (error?.status >= 400 && error?.status < 500) return false;
        return failureCount < 1; // محاولة واحدة فقط
      },
    },
  },
});

export const MatineTheme = createTheme({
  primaryColor: "primary",
  defaultRadius: "lg",
  colors: {
    primary: [
      "#e8f7ff",
      "#d9e9f6",
      "#b4d0e6",
      "#8cb6d7",
      "#6b9fca",
      "#5691c3",
      "#498ac1",
      "#3d81b8",
      "#2d6a9a",
      "#195c89",
    ],
  },
  focusRing: "always",
  // fontFamily: "Cairo, sans-serif",
  // headings: { fontFamily: "Open Sans, sans-serif" },
  components: {
    // ActionIcon: ActionIcon.extend({
    //   defaultProps: {
    //     variant: "subtle",
    //   },
    // }),
    Loader: Loader.extend({
      defaultProps: {
        type: "bars",
      },
    }),
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      {/* <NetworkOptimizer /> */}
      <DirectionProvider detectDirection>
        <MantineProvider theme={MatineTheme}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              {/* <Layout> */}
              <Suspense
                fallback={
                  <div className="min-h-screen flex items-center justify-center">
                    <UnifiedLoadingSpinner size="md" />
                  </div>
                }
              >
                <Routes>
                  <Route path="/">
                    <Route index path="/auth" element={<Auth />} />
                    <Route
                      path="/auth/reset-password"
                      element={<ResetPassword />}
                    />
                    <Route
                      path="/auth/forget-password"
                      element={<ForgotPassword />}
                    />
                    <Route
                      path="/login"
                      element={<Navigate to="/auth" replace />}
                    />
                    <Route
                      path="/admin-login"
                      element={<Navigate to="/auth" replace />}
                    />
                    <Route
                      path="/school-login"
                      element={<Navigate to="/auth" replace />}
                    />
                    {/* <Route
                    path="/"
                    element={
                      // <ProtectedRoute>
                      <Navigate to="/auth" replace />
                      //// </ProtectedRoute>
                    }
                  /> */}
                    <Route path="/" element={<Layout />}>
                      <Route index element={<Navigate to="/auth" />} />

                      <Route
                        path="/schools/:schoolId"
                        element={<SchoolLayout />}
                      >
                        <Route index element={<Navigate to="dashboard" />} />
                        <Route
                          path="dashboard"
                          element={
                            //// <ProtectedRoute requiredRole="school">
                            <General />
                          }
                        />{" "}
                        <Route
                          path="attendance"
                          element={
                            //// <ProtectedRoute requiredRole="school">
                            <SimpleAttendanceFixed />
                            // </ProtectedRoute>
                          }
                        />
                        <Route
                          path="late-attendance"
                          element={
                            // <ProtectedRoute requiredRole="school">
                            <LateAttendance />
                            // </ProtectedRoute>
                          }
                        />
                        <Route
                          path="reports-beta"
                          element={
                            // <ProtectedRoute requiredRole="school">
                            <ReportsBeta />
                            // </ProtectedRoute>
                          }
                        />
                        <Route
                          path="school-settings"
                          element={
                            // <ProtectedRoute requiredRole="school">
                            <SchoolSettings />
                            // </ProtectedRoute>
                          }
                        />{" "}
                        <Route
                          path="students"
                          element={
                            // <ProtectedRoute requiredRole="school">
                            <Students />
                            // </ProtectedRoute>
                          }
                        />
                        <Route
                          path="teachers"
                          element={
                            // <ProtectedRoute requiredRole="school">
                            <Teachers />
                            // </ProtectedRoute>
                          }
                        />
                        <Route
                          path="school-help"
                          element={
                            // <ProtectedRoute requiredRole="school">
                            <SchoolHelp />
                            // </ProtectedRoute>
                          }
                        />
                        <Route
                          path="student-late-attendance"
                          element={
                            // <ProtectedRoute requiredRole="school">
                            <StudentLateAttendance />
                            // </ProtectedRoute>
                          }
                        />{" "}
                        <Route
                          path="student-attendance"
                          element={
                            // <ProtectedRoute requiredRole="school">
                            <StudentAttendance />
                            // </ProtectedRoute>
                          }
                        />{" "}
                      </Route>
                      <Route
                        path="/profile"
                        element={
                          // <ProtectedRoute>
                          <Profile />
                          //  </ProtectedRoute>
                        }
                      />
                      <Route
                        path="admin"
                        element={
                          // <ProtectedRoute requiredRole="admin">

                          <AdminLayout>
                            <AdminDashboard />
                          </AdminLayout>
                          // </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/content-management"
                        element={
                          // <ProtectedRoute requiredRole="admin">
                          <ContentManagement />
                          // </ProtectedRoute>
                        }
                      />
                    </Route>
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="*" element={<NotFound />} />{" "}
                  </Route>
                </Routes>
              </Suspense>
              {/* </Layout> */}
            </BrowserRouter>
            {/* <PerformanceMonitor /> */}
          </TooltipProvider>
        </MantineProvider>
      </DirectionProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
